/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import CopyToClipboard from "react-copy-to-clipboard";
import { Link } from "react-router-dom";
import { Cell } from "react-table";
import { FlexContainer, SVGIcon, Text, theme } from "@nordcloud/gnui";
import { EstateCostCell } from "~/components/Tables/Cells";
import { SUCCESS_TEXT } from "~/constants";
import { showSimpleInfo } from "~/services/toast";
import { isNotNil, trimTextBeginning } from "~/tools";
import { generateRowData } from "~/views/estate/helpers";

type Row = ReturnType<typeof generateRowData>;

export const KUBERNETES_METRICS_COLUMNS = [
  {
    Header: "Resource name / Resource type",
    accessor: "nodclouId",
    disableSortBy: true,
    width: "30%",
    Cell: ({ row }: Cell<Row>) => (
      <>
        <FlexContainer>
          <Link to={`/estate/${row.original.id}`}>
            {trimTextBeginning(row.original.name, 70)}{" "}
          </Link>
          <CopyToClipboard
            text={row.original.name ?? ""}
            onCopy={() => {
              showSimpleInfo(SUCCESS_TEXT.copied);
            }}
          >
            <SVGIcon
              name="copy"
              size="sm"
              css={{ cursor: "pointer", marginLeft: theme.spacing.spacing02 }}
            />
          </CopyToClipboard>
        </FlexContainer>
        <Text
          size="sm"
          tag="div"
          style={{ wordBreak: "break-all" }}
          color={theme.color.text.text02}
        >
          {row.original.type}
        </Text>
      </>
    ),
  },
  {
    Header: "Application / Environment",
    accessor: "application/env",
    disableSortBy: true,
    width: "15%",
    Cell: ({ row }: Cell<Row>) => (
      <>
        <FlexContainer>
          <Link
            to={`/applications/${row.original.environment?.application.nid}`}
          >
            {trimTextBeginning(row.original.environment?.application.name, 70)}{" "}
          </Link>
        </FlexContainer>
        <Text size="sm" tag="div" style={{ wordBreak: "break-all" }}>
          {row.original.environment?.name ?? ""}
        </Text>
      </>
    ),
  },
  {
    Header: () => <FlexContainer justifyContent="flex-end">CPU</FlexContainer>,
    accessor: "cpu",
    disableSortBy: true,
    width: "5rem",
    Cell: ({ row }: Cell<Row>) => {
      // Memory and cpu arrays will always consist of 1 element
      const cpu = row.original.resourceConsumption?.cpu ?? null;
      const usedCpu = isNotNil(cpu) ? Number(cpu[0].used) : null;

      return (
        <>
          {isNotNil(usedCpu) ? (
            <Text
              size="md"
              tag="div"
              style={{ wordBreak: "break-all", textAlign: "right" }}
            >
              {convertCpuCores(usedCpu)}
            </Text>
          ) : (
            noData
          )}
        </>
      );
    },
  },
  {
    Header: () => (
      <FlexContainer justifyContent="flex-end">Storage (Write)</FlexContainer>
    ),
    accessor: "storageWrite",
    disableSortBy: true,
    width: "5rem",
    Cell: ({ row }: Cell<Row>) => {
      const storage = row.original.resourceConsumption?.storageRw;

      return (
        <>
          {isNotNil(storage) ? (
            <Text
              size="md"
              tag="div"
              style={{ wordBreak: "break-all", textAlign: "right" }}
            >
              {convertBytes(Number(storage[0].write))}
            </Text>
          ) : (
            noData
          )}
        </>
      );
    },
  },
  {
    Header: () => (
      <FlexContainer justifyContent="flex-end">Storage (Read)</FlexContainer>
    ),
    accessor: "storageRead",
    disableSortBy: true,
    width: "5rem",
    Cell: ({ row }: Cell<Row>) => {
      const storage = row.original.resourceConsumption?.storageRw;

      return (
        <>
          {isNotNil(storage) ? (
            <Text
              size="md"
              tag="div"
              style={{ wordBreak: "break-all", textAlign: "right" }}
            >
              {convertBytes(Number(storage[0].read))}
            </Text>
          ) : (
            noData
          )}
        </>
      );
    },
  },
  {
    Header: () => (
      <FlexContainer justifyContent="flex-end">Memory</FlexContainer>
    ),
    accessor: "memory",
    disableSortBy: true,
    width: "5rem",
    Cell: ({ row }: Cell<Row>) => {
      const memory = row.original.resourceConsumption?.memory;

      return (
        <>
          {isNotNil(memory) ? (
            <Text
              size="md"
              tag="div"
              style={{ wordBreak: "break-all", textAlign: "right" }}
            >
              {convertBytes(Number(memory[0].used))}
            </Text>
          ) : (
            noData
          )}
        </>
      );
    },
  },
  {
    Header: () => (
      <FlexContainer justifyContent="flex-end">Cost / Forecast</FlexContainer>
    ),
    accessor: "actualCosts",
    disableSortBy: true,
    width: "5rem",
    hasLeftBorder: true,
    Cell: ({ row }: Cell<Row>) => (
      <EstateCostCell
        estateId={row.original.id ?? ""}
        isWaste={row.original.isWaste}
        reason={row.original.reason}
        value={row.original.actualCosts}
        amount={row.original.savingsAmount ?? ""}
        count={row.original.savingsAmountCount ?? 0}
        extraInfo={row.original.forecast}
      />
    ),
  },
];

const noData = (
  <Text
    size="sm"
    tag="div"
    style={{ wordBreak: "break-all", textAlign: "right" }}
    color={theme.color.text.text02}
  >
    -
  </Text>
);

export function convertCpuCores(mCores: number) {
  return mCores < 1000
    ? `${Math.round(mCores)} mCore`
    : `${Math.round(mCores / 1000)} Core`;
}

export function convertBytes(bytes: number) {
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const index = bytes > 1 ? Math.floor(Math.log(bytes) / Math.log(k)) : 0;
  const finalNumb =
    bytes > 0 ? parseFloat((bytes / Math.pow(k, index)).toFixed(2)) : 0;

  return `${finalNumb} ${sizes[index]}`;
}
